import { useState, useEffect } from "react";
import { Copy, RotateCw, PlayCircle, Loader2, Eye, EyeOff, X, ShieldAlert, CheckCircle2, Link2, Database } from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminWebhookUrls,
    rotateAdminPreCallSecret,
    rotateAdminVoiceIntakeSecret,
    saveAdminPostCallSecret,
    rotateAdminExistingJobManagerSecret,
    rotateAdminCompanyContextSecret,
    fetchAdminCompanyAiData,
    rotateAdminTradeContextSecret,
    testAdminPreCall,
    testAdminVoiceIntake,
    testAdminPostCall,
} from "@/lib/api";

/**
 * Admin-only ElevenLabs tools panel. Renders three purpose-grouped rows:
 *   Pre-call   — copy URL, regenerate secret, run synthetic test
 *   Voice Intake — copy URL, regenerate secret, run synthetic test
 *   Post-Call    — regenerate secret, verify HMAC
 * Plaintext secrets are shown EXACTLY ONCE inside a reveal modal after
 * a successful rotate. Read responses never leak secrets.
 */
export default function AdminIntegrationTools({ companyId, onReload }) {
    const [urls, setUrls] = useState(null);
    const [busy, setBusy] = useState(null);
    const [reveal, setReveal] = useState(null);
    const [aiData, setAiData] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    const load = async () => {
        try { setUrls(await fetchAdminWebhookUrls(companyId)); }
        catch { toast.error("Kunne ikke hente webhook-URL'er"); }
    };
    useEffect(() => { if (companyId) load(); }, [companyId]);

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} kopieret`);
        } catch { toast.error("Kunne ikke kopiere"); }
    };

    const doRotate = async (fn, label, kind) => {
        setBusy(kind);
        try {
            const r = await fn(companyId);
            // Show the reveal modal FIRST. Do NOT trigger a parent reload here —
            // that would unmount this component and close the modal automatically.
            // The parent reload is deferred until the modal is dismissed via
            // `handleRevealClose` below.
            setReveal({ secret: r.secret, label, rotated_at: r.rotated_at });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke regenerere");
        } finally { setBusy(null); }
    };

    // Called when the user actively dismisses the reveal modal (click X or
    // "Jeg har kopieret det"). Only THEN do we refresh local URLs + parent.
    const handleRevealClose = () => {
        setReveal(null);
        load();          // refresh configured/rotated_at indicators
        onReload?.();    // notify parent (may re-fetch company doc)
    };

    const doTest = async (fn, kind) => {
        setBusy(kind);        try {
            const r = await fn(companyId);
            if (r.ok) {
                toast.success(r.message || "Test bestået", { description: JSON.stringify(r.checks || r.response_payload || {}) });
            } else {
                toast.error(r.message || "Test fejlede", { description: JSON.stringify(r.checks || {}) });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test fejlede");
        } finally { setBusy(null); }
    };

    const loadAiData = async () => {
        setAiLoading(true);
        try { setAiData(await fetchAdminCompanyAiData(companyId)); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente AI-data"); }
        finally { setAiLoading(false); }
    };

    if (!urls) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center" data-testid="admin-tools-loading">
                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
            </div>
        );
    }

    return (
        <>
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="admin-integration-tools">
                <header className="px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-amber-700" />
                    <p className="text-sm font-bold uppercase tracking-wider text-amber-900">ADMIN-VÆRKTØJER</p>
                    <span className="text-xs text-amber-700 ml-1">— kun super-admin</span>
                </header>
                <div className="divide-y divide-amber-100">
                    <ToolRow
                        title="Pre-call"
                        url={urls.pre_call.url}
                        configured={urls.pre_call.configured}
                        rotated_at={urls.pre_call.rotated_at}
                        onCopy={() => copy(urls.pre_call.url, "Pre-call URL")}
                        onRotate={() => doRotate(rotateAdminPreCallSecret, "Pre-call secret", "rot-pre")}
                        onTest={() => doTest(testAdminPreCall, "test-pre")}
                        busyRotate={busy === "rot-pre"}
                        busyTest={busy === "test-pre"}
                        testid="tool-pre-call"
                    />
                    <ToolRow
                        title="Voice Intake"
                        url={urls.voice_intake.url}
                        configured={urls.voice_intake.configured}
                        rotated_at={urls.voice_intake.rotated_at}
                        onCopy={() => copy(urls.voice_intake.url, "Voice Intake URL")}
                        onRotate={() => doRotate(rotateAdminVoiceIntakeSecret, "Voice Intake secret", "rot-vi")}
                        onTest={() => doTest(testAdminVoiceIntake, "test-vi")}
                        busyRotate={busy === "rot-vi"}
                        busyTest={busy === "test-vi"}
                        testid="tool-voice-intake"
                    />
                    <div data-testid="tool-post-call-wrap">
                        <ToolRow
                            title="Post-Call"
                            url={urls.post_call.url}
                            configured={urls.post_call.configured}
                            rotated_at={urls.post_call.rotated_at}
                            onCopy={() => copy(urls.post_call.url, "Post-Call URL")}
                            onTest={() => doTest(testAdminPostCall, "test-pc")}
                            busyTest={busy === "test-pc"}
                            testid="tool-post-call"
                        />
                        <PostCallSecretForm companyId={companyId} configured={urls.post_call.configured} onSaved={load} />
                    </div>
                    {urls.existing_job_manager && (
                        <ToolRow
                            title="Existing Job Manager"
                            url={urls.existing_job_manager.url}
                            configured={urls.existing_job_manager.configured}
                            rotated_at={urls.existing_job_manager.rotated_at}
                            onCopy={() => copy(urls.existing_job_manager.url, "Existing Job Manager URL")}
                            onRotate={() => doRotate(rotateAdminExistingJobManagerSecret, "Existing Job Manager secret", "rot-ejm")}
                            busyRotate={busy === "rot-ejm"}
                            testid="tool-existing-job-manager"
                        />
                    )}
                    {urls.company_context && (
                        <div data-testid="tool-company-context-wrap">
                            <ToolRow
                                title="Company Context"
                                url={urls.company_context.url}
                                configured={urls.company_context.configured}
                                rotated_at={urls.company_context.rotated_at}
                                onCopy={() => copy(urls.company_context.url, "Company Context URL")}
                                onRotate={() => doRotate(rotateAdminCompanyContextSecret, "Company Context secret", "rot-ctx")}
                                busyRotate={busy === "rot-ctx"}
                                testid="tool-company-context"
                            />
                            <div className="px-5 pb-3 -mt-1 flex flex-wrap items-center gap-3" data-testid="company-context-status">
                                <span className="text-[11px] text-slate-500">
                                    Profil v{urls.company_context.profile_version || 0} ·{" "}
                                    <b className={urls.company_context.profile_status === "published" ? "text-emerald-700" : "text-amber-700"}>
                                        {urls.company_context.profile_status === "published" ? "Publiceret" : "Ikke publiceret"}
                                    </b>{" "}· {urls.company_context.completeness_percent || 0}% udfyldt
                                </span>
                                <ToolBtn icon={Database} onClick={loadAiData} label="Se AI-data" testid="tool-company-context-view" busy={aiLoading} />
                            </div>
                        </div>
                    )}
                    {urls.trade_context && (
                        <div data-testid="tool-trade-context-wrap">
                            <ToolRow
                                title="Trade Context"
                                url={urls.trade_context.url}
                                configured={urls.trade_context.configured}
                                rotated_at={urls.trade_context.rotated_at}
                                onCopy={() => copy(urls.trade_context.url, "Trade Context URL")}
                                onRotate={() => doRotate(rotateAdminTradeContextSecret, "Trade Context secret", "rot-tctx")}
                                busyRotate={busy === "rot-tctx"}
                                testid="tool-trade-context"
                            />
                            <div className="px-5 pb-3 -mt-1 text-[11px] text-slate-500" data-testid="trade-context-trades">
                                Primær: <b className="text-slate-700">{urls.trade_context.primary_trade || "—"}</b>
                                {" · "}Aktive: <b className="text-slate-700">{(urls.trade_context.enabled_trades || []).join(", ") || "—"}</b>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {reveal && <SecretRevealModal reveal={reveal} onClose={handleRevealClose} />}
            {aiData && <AiDataModal data={aiData} onClose={() => setAiData(null)} />}
        </>
    );
}

function ToolRow({ title, url, configured, rotated_at, onCopy, onRotate, onTest, busyRotate, busyTest, testid }) {
    return (
        <div className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={testid}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{title}</p>
                    {configured ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                            <CheckCircle2 className="w-2.5 h-2.5" /> Klar
                        </span>
                    ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                            Ikke sat
                        </span>
                    )}
                    {rotated_at && (
                        <span className="text-[10px] text-slate-400">roteret {new Date(rotated_at).toLocaleDateString("da-DK")}</span>
                    )}
                </div>
                {url && (
                    <p className="text-[11px] text-slate-500 font-mono truncate mt-0.5" title={url}>{url}</p>
                )}
            </div>
            <div className="flex gap-1.5 shrink-0">
                <ToolBtn icon={Copy} onClick={onCopy} label="Kopiér URL" testid={`${testid}-copy`} />
                <ToolBtn icon={RotateCw} onClick={onRotate} label="Regenerér" testid={`${testid}-rotate`} busy={busyRotate} tone="warning" />
                {onTest && <ToolBtn icon={PlayCircle} onClick={onTest} label="Test" testid={`${testid}-test`} busy={busyTest} tone="solid" />}
            </div>
        </div>
    );
}

function ToolBtn({ icon: Icon, onClick, label, testid, busy, tone = "ghost" }) {
    const cls = tone === "solid"
        ? "bg-slate-900 hover:bg-slate-700 text-white border-slate-900"
        : tone === "warning"
        ? "bg-white hover:bg-amber-50 text-amber-800 border-amber-200"
        : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200";
    return (
        <button type="button" onClick={onClick} disabled={busy} data-testid={testid}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition disabled:opacity-50 ${cls}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
            {label}
        </button>
    );
}

function SecretRevealModal({ reveal, onClose }) {
    const [visible, setVisible] = useState(false);
    const copy = async () => {
        try { await navigator.clipboard.writeText(reveal.secret); toast.success("Kopieret"); }
        catch { toast.error("Kunne ikke kopiere"); }
    };
    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" data-testid="secret-reveal-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
                <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-amber-600" />
                        <h3 className="text-base font-bold text-slate-900">{reveal.label} — vis kun én gang</h3>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
                </header>
                <div className="p-6 space-y-3">
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <strong>Kopiér nu.</strong> Denne secret vises kun én gang og kan ikke hentes igen. Indsæt den straks i ElevenLabs-konfigurationen.
                    </div>
                    <div className="relative">
                        <input type={visible ? "text" : "password"} readOnly value={reveal.secret}
                            data-testid="secret-reveal-input"
                            className="w-full pr-24 pl-3 py-2.5 rounded-lg border border-slate-200 font-mono text-sm bg-slate-50" />
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
                            <button type="button" onClick={() => setVisible((v) => !v)} data-testid="secret-reveal-toggle"
                                className="p-1.5 rounded hover:bg-slate-200">
                                {visible ? <EyeOff className="w-3.5 h-3.5 text-slate-600" /> : <Eye className="w-3.5 h-3.5 text-slate-600" />}
                            </button>
                            <button type="button" onClick={copy} data-testid="secret-reveal-copy"
                                className="p-1.5 rounded hover:bg-slate-200">
                                <Copy className="w-3.5 h-3.5 text-slate-600" />
                            </button>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">
                        Roteret: <span className="font-mono">{new Date(reveal.rotated_at).toLocaleString("da-DK")}</span>
                    </p>
                </div>
                <footer className="px-6 py-3 border-t border-slate-100 flex justify-end">
                    <button onClick={onClose} data-testid="secret-reveal-close"
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-700">
                        Jeg har kopieret det
                    </button>
                </footer>
            </div>
        </div>
    );
}

function AiDataModal({ data, onClose }) {
    return (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4" data-testid="ai-data-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
                <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-600" />
                        <h3 className="text-base font-bold text-slate-900">Company Context — AI-data</h3>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="ai-data-modal-close"><X className="w-4 h-4 text-slate-500" /></button>
                </header>
                <div className="p-6 space-y-3 overflow-auto">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>Status: <b className="text-slate-700">{data.profile_status}</b></span>
                        <span>Version: <b className="text-slate-700">{data.profile_version}</b></span>
                        <span>Udfyldt: <b className="text-slate-700">{data.completeness_percent}%</b></span>
                    </div>
                    {data.missing_fields?.length > 0 && (
                        <div className="text-xs text-amber-700">Mangler: {data.missing_fields.join(", ")}</div>
                    )}
                    {data.published ? (
                        <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-xl p-4 overflow-auto" data-testid="ai-data-modal-json">
{JSON.stringify(data.payload, null, 2)}
                        </pre>
                    ) : (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                            Virksomheden har ikke publiceret en AI-profil endnu.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function PostCallSecretForm({ companyId, configured, onSaved }) {
    const [secret, setSecret] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const val = secret.trim();
        if (val.length < 8) { toast.error("Indsæt det fulde ElevenLabs webhook-secret"); return; }
        setSaving(true);
        try {
            await saveAdminPostCallSecret(companyId, val);
            toast.success("ElevenLabs HMAC-secret gemt");
            setSecret("");
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke gemme secret");
        } finally { setSaving(false); }
    };

    return (
        <div className="px-5 pb-4 -mt-1" data-testid="post-call-secret-form">
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-700">Gem ElevenLabs HMAC-secret</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="post-call-secret-status">
                        {configured ? "Konfigureret" : "Mangler"}
                    </span>
                </div>
                <p className="text-[11px] text-slate-500 mb-2">Indsæt det shared secret, ElevenLabs viste da webhooket blev oprettet. Det bruges til HMAC-verifikation og vises aldrig igen efter du gemmer.</p>
                <div className="flex items-center gap-2">
                    <input
                        type="password"
                        autoComplete="off"
                        value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        placeholder="wsec_… fra ElevenLabs"
                        data-testid="post-call-secret-input"
                        className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="post-call-secret-save"
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1.5">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}Gem
                    </button>
                </div>
            </div>
        </div>
    );
}
